import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useClerk } from '@clerk/clerk-react';
import { Box, Button, Chip, Divider, FormControlLabel, Switch, Typography } from '@mui/material';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import SecurityIcon from '@mui/icons-material/Security';
import SectionCard from './SectionCard';
import { useUserContext } from '../../hooks/useUserContext';
import billingService from '../../services/billingService';
import addonAssignmentService, { ADDON_DISPLAY_NAMES } from '../../services/addonAssignmentService';
import cpeHistoryService from '../../services/cpeHistoryService';
import type { CreditImportStatus } from '../../services/cpeHistoryService';

/** Account security (Clerk), billing/subscription management, and nightly credit-import preferences. */
export default function AccountCard() {
  const { backendProfile, isTenantAdmin } = useUserContext();
  const { openUserProfile } = useClerk();
  const navigate = useNavigate();
  const [activeAddonIds, setActiveAddonIds] = useState<string[]>([]);
  // External credit import sources (PICPA/Aptify today, CPAx later) — one nightly toggle each.
  const [creditImports, setCreditImports] = useState<CreditImportStatus[]>([]);
  const [savingSource, setSavingSource] = useState<string | null>(null);

  useEffect(() => {
    if (!backendProfile) return;
    const load = async () => {
      try {
        if (backendProfile.tenantId && backendProfile.id) {
          const assigned = await addonAssignmentService.getUserAssignedAddonIds(backendProfile.tenantId, backendProfile.id);
          if (assigned.length > 0) {
            setActiveAddonIds(assigned);
            return;
          }
        }
        const status = await billingService.getUserSubscriptionStatus();
        if (status.hasActiveSubscription && status.subscriptionSource === 'user' && status.userSubscription) {
          setActiveAddonIds(status.userSubscription.activeAddonIds);
        }
      } catch {
        // Module chips are supplemental; the billing page is the source of truth.
      }
    };
    load();
  }, [backendProfile]);

  useEffect(() => {
    let cancelled = false;
    cpeHistoryService
      .getCreditImports()
      .then((list) => {
        if (!cancelled) setCreditImports(list.filter((s) => s.available));
      })
      .catch(() => {
        // Preference is supplemental; without status the subsection stays hidden.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleAutoSyncToggle = async (source: string, enabled: boolean) => {
    setSavingSource(source);
    setCreditImports((prev) => prev.map((s) => (s.source === source ? { ...s, autoSyncEnabled: enabled } : s)));
    try {
      await cpeHistoryService.updateCreditImportPreferences(source, { autoSyncEnabled: enabled });
    } catch {
      setCreditImports((prev) => prev.map((s) => (s.source === source ? { ...s, autoSyncEnabled: !enabled } : s)));
    } finally {
      setSavingSource(null);
    }
  };

  return (
    <SectionCard icon={<SecurityIcon />} title="Account">
      {/* Security & email */}
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
        Security & Email
      </Typography>
      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
        Manage email addresses, change your password, enable two-factor authentication, and other security settings.
      </Typography>
      <Button variant="outlined" startIcon={<ManageAccountsIcon />} onClick={() => openUserProfile({ apiKeysProps: { hide: true } })}>
        Manage Account Settings
      </Button>

      <Divider sx={{ my: 3 }} />

      {/* Billing & subscription */}
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
        Billing & Subscription
      </Typography>
      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
        View your current subscription, manage modules, or choose a new plan.
      </Typography>
      {activeAddonIds.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="overline" sx={{ color: 'text.secondary', lineHeight: 1.5 }}>
            Active Modules
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 0.5 }}>
            {activeAddonIds.map(id => (
              <Chip key={id} label={ADDON_DISPLAY_NAMES[id] ?? id} color="primary" variant="outlined" size="small" />
            ))}
          </Box>
        </Box>
      )}
      <Button
        variant="contained"
        startIcon={<CreditCardIcon />}
        onClick={() => navigate(isTenantAdmin ? '/app/tenant-admin/billing' : '/app/billing')}
      >
        Manage Billing
      </Button>

      {creditImports.map((imp) => (
        <Box key={imp.source}>
          <Divider sx={{ my: 3 }} />

          {/* Same toggle the CPE history import prompt offers after a yes */}
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
            {imp.displayName} Credits
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
            Import CPE you've earned through {imp.displayName} into your credit history each night.
          </Typography>
          <FormControlLabel
            control={
              <Switch
                checked={imp.autoSyncEnabled}
                disabled={savingSource === imp.source}
                onChange={(_, checked) => void handleAutoSyncToggle(imp.source, checked)}
              />
            }
            label="Keep in sync nightly"
          />
        </Box>
      ))}
    </SectionCard>
  );
}
